import type {
  ComponentPropsWithRef,
  FC,
  InputHTMLAttributes,
  ReactNode,
} from 'react';
import { useId } from 'react';
import { ErrorMessage } from 'src/components/form/error-message';
import { FieldItem, FieldLayout } from 'src/components/form/field-layout';
import type { FormFieldProps } from 'src/components/form/form-field-props';
import { FormHint } from 'src/components/form/form-hint';
import {
  assertUniqueOptionValues,
  type BaseFormOption,
} from 'src/components/form/form-option';
import { FieldLabel } from 'src/components/form/label';
import { useCustomField } from 'src/components/form/use-custom-field';
import { useFocusManager } from 'src/components/form/use-focus-manager';
import { Tooltip } from 'src/components/overlay/tooltip';
import { useDefaultStacked } from 'src/hooks/use-default-stacked';
import { cn } from 'src/utils/cn';

export const RadioLabel: FC<{
  showError?: boolean;
  disabled?: boolean;
  children: ReactNode;
}> = ({ showError, disabled, children }) => {
  return (
    <span
      className={cn(
        'cursor-pointer select-none',
        showError && 'text-foreground-support-error',
        disabled && 'cursor-not-allowed text-foreground-surface-disabled'
      )}
    >
      {children}
    </span>
  );
};

type RadioInputProps = ComponentPropsWithRef<'input'> & {
  showError: boolean;
};

export const RadioInput: FC<RadioInputProps> = ({
  disabled,
  showError,
  ...rest
}) => {
  return (
    <span>
      <input
        type="radio"
        {...rest}
        className={cn(
          `
            absolute w-px overflow-hidden p-0 [clip:rect(0_0_0_0)]
            focus:outline-none
            focus-visible:outline-none
          `,
          rest.className
        )}
        disabled={disabled}
      />
      <div
        style={
          {
            '--icon-size': '1.5rem',
          } as React.CSSProperties
        }
        className={cn(
          `
            inline-block size-(--icon-size) cursor-pointer rounded-full
            bg-border-surface-default
            align-[calc(var(--spacing-icon-offset)*-1)]
          `,
          `
            after:block after:size-[1.3rem]
            after:translate-[calc((var(--icon-size)-1.3rem)/2)]
            after:rounded-full after:bg-white
          `,
          `
            [:checked~&]:after:block [:checked~&]:after:size-[0.6rem]
            [:checked~&]:after:translate-[calc((var(--icon-size)-0.6rem)/2)]
            [:checked~&]:after:rounded-full
          `,
          `
            [:focus-visible~&]:focus-style [:focus-visible~&]:spaced-focus-style
            [:focus~&]:focus-style [:focus~&]:spaced-focus-style
          `,
          showError &&
            `
              bg-border-support-error-subdued
              after:bg-background-support-error-minimal
            `,
          disabled &&
            `
              cursor-not-allowed bg-border-surface-disabled
              after:border-border-surface-disabled
              after:bg-background-surface-disabled
            `
        )}
      />
    </span>
  );
};

type SimpleRadioProps = ComponentPropsWithRef<'input'> & {
  label: string;
  showError: boolean;
};

const SimpleRadio: FC<SimpleRadioProps> = ({
  showError,
  label,
  ...inputProps
}) => {
  return (
    <label className="inline-grid grid-flow-col items-baseline justify-start gap-xs">
      {/* empty span needed for firefox */}
      <span>
        <RadioInput showError={showError} {...inputProps} />
      </span>
      <RadioLabel showError={showError} disabled={inputProps.disabled}>
        {label}
      </RadioLabel>
    </label>
  );
};

type RadiosProps = {
  options: BaseFormOption<string>[];
  stacked?: boolean;
  stackedOptions?: boolean;
} & FormFieldProps &
  InputHTMLAttributes<HTMLInputElement>;

/**
 * Use this if you have a short list of options (e.g. 2-5) where the user can only select one.
 *
 * If you have a long list of options, consider using the [Single Select](./?path=/docs/components-form-single-select--docs) component.
 *
 * If you have a long list of options where you want to search, consider using the [Searchable Single Select](./?path=/docs/components-form-searchable-single-select--docs) component.
 */
export const Radios: FC<RadiosProps> = (props) => {
  const { isStacked } = useDefaultStacked();
  const {
    label,
    name,
    options,
    checked,
    stacked = isStacked,
    autoFocus,
    stackedOptions,
    hint,
    hideLabel = false,
    info,
    markAsRequired,
    disabledMessage,
    ...inputProps
  } = props;
  assertUniqueOptionValues(options);

  const fieldId = useId();

  const [field, showError, error] = useCustomField(name, label);
  const focusRef = useFocusManager<HTMLInputElement>();

  const displayLabel = `${label}${markAsRequired ? '*' : ''}`;

  return (
    <FieldLayout stacked={stacked}>
      {!hideLabel && (
        <FieldLabel
          name={name}
          displayLabel={displayLabel}
          fieldId={fieldId}
          info={info}
          showError={showError}
        />
      )}

      <FieldItem>
        {/* div is needed for layout */}
        <div>
          <div
            className={cn(
              'inline-grid items-start',
              // same as checkbox group
              stackedOptions ? `grid-flow-row gap-xs` : 'grid-flow-col gap-3xl'
            )}
            data-testid={field.name}
            data-input-type="radio" // used in unit tests
            data-input-value={field.value} // used in unit tests
          >
            {options.map((option, index) => (
              <Tooltip
                content={
                  inputProps.disabled && disabledMessage
                    ? disabledMessage
                    : undefined
                }
                key={option.value}
              >
                {(targetProps) => (
                  <SimpleRadio
                    ref={focusRef}
                    label={option.label}
                    showError={showError}
                    autoFocus={index === 0 && autoFocus}
                    id={index === 0 ? fieldId : undefined}
                    data-testid={`${field.name}:${option.value}`}
                    {...field}
                    {...inputProps}
                    value={option.value}
                    checked={option.value === field.value}
                    disabled={inputProps.disabled}
                    {...targetProps}
                  />
                )}
              </Tooltip>
            ))}
          </div>
        </div>

        {hint && <FormHint isDisabled={props.disabled}>{hint}</FormHint>}

        {showError && error && (
          <ErrorMessage
            data-testid={`${name}Error`}
            error={error}
            label={label}
          />
        )}
      </FieldItem>
    </FieldLayout>
  );
};
